import React from "react";
import { func, bool, string, object } from "prop-types";
import { Link } from "react-router-dom";

import { Header } from "../BasicInfo";

import { InputFile, Button, Loading } from "../../common";
import { openCv } from "../../helpers/helpers";

import * as style from "./styles.module.scss";
import OnboardImage from "../OnboardImage";
import { trackOnboardingStep } from "../../helpers/productMetrics";

const CvHeaderProfile = ({ hasExistingCv }) => (
  <Header>
    <h2>{hasExistingCv ? "Update your CV" : "Upload your CV"}</h2>
  </Header>
);

CvHeaderProfile.propTypes = {
  hasExistingCv: string.isRequired,
};

const CvUpload = ({
  appData,
  isFileSelected,
  fileName,
  selectFile,
  handleSubmit,
  isCvLoading,
  isOnboarding,
  validationError,
}) => {
  const hasExistingCv = appData.cv && appData.cv.fileKey;

  return (
    <div className="row h-100">
      {isOnboarding ? (
        <OnboardImage
          title="Great work!"
          subtitle="Now we just need your CV / work history"
          mobileDescription="Providing a CV or work history is really important to help us match you to the right opportunities."
          desktopDescription="Providing a CV or work history is really important to help us match you to the right opportunities."
          imagePath="/images/cv_info.svg"
          className={style.cvInfo__imageBackground}
          imageClassName={style.cvInfo__image}
        />
      ) : (
        <CvHeaderProfile hasExistingCv={hasExistingCv} />
      )}
      <div className={`col-sm-12 col-lg-6 mt-5 mt-lg-0 ${style.cvInfo__form}`}>
        <div className="mb-auto d-flex flex-column justify-content-center align-items-center h-100">
          {(hasExistingCv || isCvLoading) && (
            <div className={`text-center ${style.cvInfo__text}`}>
              <p>
                You can view your existing CV here or upload a new one below.
              </p>
              <Button
                onClick={() => openCv(appData.cv)}
                disabled={isCvLoading}
                classes="btn btn-primary btn-block m-2 mx-auto"
              >
                {isCvLoading ? <Loading /> : "View your current CV"}
              </Button>
            </div>
          )}
          <div className={`text-center mb-2`}>
            Your CV must be provided as <b>doc</b>, <b>docx</b> or <b>PDF</b>{" "}
            files.
          </div>
          {isOnboarding && !isFileSelected && (
            <div className={`${style.cvInfo__link} mb-2`}>
              Don&apos;t have a CV?&nbsp;
              <Link
                to="/onboard/work-history"
                style={{ textDecoration: "underline" }}
              >
                Enter work history manually
              </Link>
            </div>
          )}
          {isFileSelected ? (
            <>
              <div className={`${style.cvInfo__imageTitle}`}>
                <p className={`h5 ${style.cvInfo__secondary}`}>
                  {validationError === "formatInvalid" && (
                    <div className={`${style.cvInfo__invalidFormat}`}>
                      This file format is invalid.
                    </div>
                  )}
                  {validationError === "fileEmpty" && "This file is empty."}
                  {!validationError && "Successfully uploaded"}
                </p>
                <p className="h6">{fileName}</p>
              </div>
              <Button
                classes={`btn btn-primary btn-block mb-1 ${style.cvInfo__secondary} ${style.cvInfo__text}`}
                onClick={selectFile}
              >
                <b>Remove file</b>
              </Button>
            </>
          ) : (
            <div className="row">
              <InputFile
                onChangeHandler={selectFile}
                text="Upload your CV"
                classes={`btn btn-info cursor-pointer ${style.cvInfo__upload}`}
                idName="cvFile"
                acceptMimeTypes=".doc,.docx,.pdf"
              />
            </div>
          )}
        </div>
        <div
          className={`mt-lg-auto my-2 mb-lg-0 ${style.cvInfo_button__container}`}
        >
          <Link
            to="/onboard/address"
            className="btn-plain d-block text-info text-center"
          >
            <button className="btn btn-outline-info text-dark mr-1">
              Back
            </button>
          </Link>
          {isFileSelected ? (
            <Button
              onClick={() => handleSubmit()}
              classes="btn btn-primary btn-block"
              disabled={validationError}
            >
              Next
            </Button>
          ) : (
            <Link to="/onboard/work-type">
              <button
                className="btn btn-outline-info text-dark mr-1"
                onClick={() => trackOnboardingStep("CV Upload")}
              >
                Skip
              </button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

CvUpload.propTypes = {
  selectFile: func.isRequired,
  handleSubmit: func.isRequired,
  isFileSelected: bool.isRequired,
  description: string.isRequired,
  fileName: string.isRequired,
  appData: object.isRequired,
  isCvLoading: bool.isRequired,
  isOnboarding: bool.isRequired,
  validationError: string,
};

export default CvUpload;
